import React, { useEffect, useState } from "react";
import { Play, ScrollText, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import Podium from "./Podium";
import Countdown from "./Countdown";
import { brand, formatUSD } from "../mock";
import { ThemeFloaters } from './ThemeFloaters';

const useCountUp = (target, duration = 1400) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const Hero = ({ topThree, end, onRules, settings, board, status = 'Live Now', periodLabel = 'Monthly Leaderboard' }) => {
  const prizePool = settings?.prizePool ?? brand.prizePool;
  const animatedPrize = useCountUp(prizePool);
  return (
    <section id="home" className="relative pt-8 pb-8" data-testid="leaderboard-hero-section">
      <ThemeFloaters page="leaderboard" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 text-center">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-4 py-1.5 ring-1 ring-emerald-400/25 mb-6 rise" data-testid="leaderboard-live-status"
          style={{ boxShadow: "0 0 12px color-mix(in srgb, var(--a-300) 30%, transparent)" }}
        >
          <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse shadow-[0_0_6px_rgba(74,222,128,0.6)]" />
          <span className="text-xs font-medium text-emerald-200 tracking-wide">
            {periodLabel} · {status}
          </span>
        </div>

        <h1 data-testid="leaderboard-hero-title" className="font-display font-extrabold tracking-normal leading-[0.95] rise">
          <span data-testid="leaderboard-hero-prize-pool" className="block headline-gradient text-6xl sm:text-7xl md:text-8xl">
            {formatUSD(animatedPrize).replace(".00", "")}
          </span>
          <span className="block text-white text-3xl sm:text-4xl md:text-5xl mt-2 tracking-normal">
            LEADERBOARD
          </span>
        </h1>

        {board === "exclusive" && (
          <div className="mt-5 flex justify-center rise" style={{ animationDelay: "0.1s" }}>
            <div className="inline-flex items-center gap-2.5 rounded-xl bg-amber-500/10 ring-1 ring-amber-400/30 px-4 py-2.5 max-w-xl text-sm text-amber-100 shadow-[0_0_16px_rgba(251,191,36,0.15)]">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
              <span>
                Wager on <span className="font-semibold text-amber-300">Stake Exclusive</span> slots only to participate in the leaderboard.
              </span>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 rise" style={{ animationDelay: "0.15s" }}>
          <a
            href="https://stake.com/?c=GreenVenom"
            data-testid="leaderboard-play-now"
            target="_blank"
            rel="noopener noreferrer"
            className="font-fancy w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-6 h-12 text-base shadow-lg shadow-emerald-500/25 transition-all hover:-translate-y-0.5"
          >
            <Play className="h-5 w-5 fill-current" />
            Play Now
          </a>
          <button
            onClick={onRules}
            data-testid="leaderboard-competition-rules"
            className="font-fancy w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/30 bg-white/5 text-white hover:bg-emerald-500/10 hover:border-emerald-400/50 font-semibold px-6 h-12 text-base transition-all hover:-translate-y-0.5"
          >
            <ScrollText className="h-5 w-5" />
            Competition Rules
          </button>
        </div>

        <div className="mt-24">
          <Podium topThree={topThree} />
        </div>

        <div className="mt-14 rise" style={{ animationDelay: "0.2s" }}>
          <Countdown end={end} />
        </div>
      </div>
    </section>
  );
};

export default Hero;
